import type {
	WorkbookEquipmentRowData,
	WorkbookSwitchPortRowData,
	WorkbookVlanLegendRowData,
} from "@/features/workbook/mock-data";

interface WorkbookSectionValidationInput {
	equipmentList: WorkbookEquipmentRowData[];
	switchPortMap: WorkbookSwitchPortRowData[];
	vlanLegend: WorkbookVlanLegendRowData[];
}

const IPV4_SEGMENT = "(?:25[0-5]|2[0-4]\\d|1\\d{2}|[1-9]?\\d)";
const IPV4_REGEX = new RegExp(`^${IPV4_SEGMENT}(?:\\.${IPV4_SEGMENT}){3}$`);
const IPV4_CIDR_REGEX = new RegExp(
	`^${IPV4_SEGMENT}(?:\\.${IPV4_SEGMENT}){3}\\/(?:[0-9]|[12]\\d|3[0-2])$`,
);
const MAC_ADDRESS_REGEX = /^[0-9a-fA-F]{2}([:-][0-9a-fA-F]{2}){5}$/;

function isValidIpv4(value: string): boolean {
	return IPV4_REGEX.test(value);
}

function isValidIpv4Cidr(value: string): boolean {
	return IPV4_CIDR_REGEX.test(value);
}

function isValidMacAddress(value: string): boolean {
	return MAC_ADDRESS_REGEX.test(value);
}

function isValidVlanId(value: string): boolean {
	const parsed = Number(value);
	return Number.isInteger(parsed) && parsed >= 1 && parsed <= 4094;
}

function isValidAllowedVlans(value: string): boolean {
	const trimmed = value.trim();
	if (trimmed.length === 0) {
		return true;
	}

	const tokens = trimmed.split(",").map((item) => item.trim());
	if (tokens.some((item) => item.length === 0)) {
		return false;
	}

	return tokens.every((token) => {
		if (token.includes("-")) {
			const parts = token.split("-").map((part) => part.trim());
			if (parts.length !== 2) {
				return false;
			}
			const [start, end] = parts;
			if (!isValidVlanId(start) || !isValidVlanId(end)) {
				return false;
			}
			return Number(start) <= Number(end);
		}

		return isValidVlanId(token);
	});
}

export function validateWorkbookSectionsForSave({
	equipmentList,
	switchPortMap,
	vlanLegend,
}: WorkbookSectionValidationInput): string[] {
	const errors: string[] = [];

	equipmentList.forEach((row, rowIndex) => {
		const displayIndex = rowIndex + 1;
		const ipAddress = row.ipAddress.trim();
		const lanMacAddress = row.lanMacAddress.trim();

		if (ipAddress.length > 0 && !isValidIpv4(ipAddress)) {
			errors.push(
				`Equipment row ${displayIndex}: IP address must be a valid IPv4 address.`,
			);
		}

		if (lanMacAddress.length > 0 && !isValidMacAddress(lanMacAddress)) {
			errors.push(
				`Equipment row ${displayIndex}: LAN MAC must be a valid MAC address (example: 00:11:22:33:44:55).`,
			);
		}
	});

	switchPortMap.forEach((row, rowIndex) => {
		const displayIndex = rowIndex + 1;
		const nativeVlan = row.nativeVlan.trim();
		const allowedVlans = row.allowedVlans.trim();

		if (nativeVlan.length > 0 && !isValidVlanId(nativeVlan)) {
			errors.push(
				`Switch Port row ${displayIndex}: Native VLAN must be an integer between 1 and 4094.`,
			);
		}

		if (allowedVlans.length > 0 && !isValidAllowedVlans(allowedVlans)) {
			errors.push(
				`Switch Port row ${displayIndex}: Allowed VLANs must be a comma-separated list of VLAN IDs or ranges (example: 10,20,30-40).`,
			);
		}
	});

	vlanLegend.forEach((row, rowIndex) => {
		const displayIndex = rowIndex + 1;
		const vlanId = row.vlanId.trim();
		const subnet = row.subnet.trim();
		const defaultGateway = row.defaultGateway.trim();

		if (vlanId.length > 0) {
			if (!isValidVlanId(vlanId)) {
				errors.push(
					`VLAN row ${displayIndex}: VLAN ID must be an integer between 1 and 4094.`,
				);
			}
		}

		if (subnet.length > 0 && !isValidIpv4Cidr(subnet)) {
			errors.push(
				`VLAN row ${displayIndex}: Subnet must be IPv4 CIDR format (example: 10.120.0.0/24).`,
			);
		}

		if (defaultGateway.length > 0 && !isValidIpv4(defaultGateway)) {
			errors.push(
				`VLAN row ${displayIndex}: Default gateway must be a valid IPv4 address.`,
			);
		}
	});

	return errors;
}
